import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { isNativeAndroid } from "@/lib/payments";
import { WebTopupNotice } from "@/components/WebTopupNotice";
import { DisconnectBank } from "@/components/DisconnectBank";
import {
  ArrowLeft, Wallet as WalletIcon, Plus, ArrowDownToLine, Loader2,
  ShoppingBag, TrendingUp, Landmark, Clock, ExternalLink,
} from "lucide-react";

const TOPUP_PRESETS = [500, 1000, 2500, 5000];
const MAX_POLL_ATTEMPTS = 8;
const POLL_INTERVAL_MS = 2500;

const fmt = (c) => `€${((c || 0) / 100).toFixed(2)}`;
const native = isNativeAndroid();

export default function WalletPage() {
  const [wallet, setWallet] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [cashouts, setCashouts] = useState([]);
  const [customTopup, setCustomTopup] = useState("");
  const [cashoutAmount, setCashoutAmount] = useState("");
  const [busy, setBusy] = useState(null);
  const [connect, setConnect] = useState(null);
  const [polling, setPolling] = useState(false);
  const pollingRef = useRef(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const refresh = useCallback(async () => {
    try {
      const [w, l, c, s] = await Promise.all([
        api.get("/wallet/me"),
        api.get("/wallet/transactions", { params: { limit: 25 } }),
        api.get("/wallet/cashouts"),
        api.get("/wallet/connect/status").catch(() => ({ data: null })),
      ]);
      setWallet(w.data);
      setLedger(l.data || []);
      setCashouts(c.data || []);
      setConnect(s.data);
      window.dispatchEvent(new CustomEvent("notelosh:wallet-changed"));
    } catch {
      toast.error("Could not load wallet");
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const pollStatus = useCallback(async (session_id, attempt = 0) => {
    if (attempt >= MAX_POLL_ATTEMPTS) {
      setPolling(false); pollingRef.current = false;
      toast.error("Top-up confirmation timed out. Refresh in a moment.");
      return;
    }
    try {
      const r = await api.get(`/checkout/status/${session_id}`);
      if (r.data.payment_status === "paid") {
        setPolling(false); pollingRef.current = false;
        toast.success("Wallet topped up!");
        navigate("/wallet", { replace: true });
        refresh();
        return;
      }
      if (r.data.status === "expired") {
        setPolling(false); pollingRef.current = false;
        toast.error("Top-up was not completed.");
        navigate("/wallet", { replace: true });
        return;
      }
    } catch { /* keep polling */ }
    setTimeout(() => pollStatus(session_id, attempt + 1), POLL_INTERVAL_MS);
  }, [navigate, refresh]);

  useEffect(() => {
    const cancelled = searchParams.get("cancelled");
    if (cancelled) {
      toast.message("Top-up cancelled");
      navigate("/wallet", { replace: true });
      return;
    }
    const sid = searchParams.get("session_id");
    if (sid && !pollingRef.current) {
      pollingRef.current = true; setPolling(true);
      pollStatus(sid, 0);
    }
    if (searchParams.get("connect") === "done") {
      toast.success("Bank details submitted — verifying with Stripe…");
      navigate("/wallet", { replace: true });
      refresh();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const topup = async (amount_cents) => {
    setBusy(`topup_${amount_cents}`);
    try {
      const r = await api.post("/wallet/topup", {
        amount_cents,
        origin_url: window.location.origin,
      });
      if (!r.data?.url) throw new Error("No checkout URL");
      window.location.href = r.data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start top-up");
      setBusy(null);
    }
  };

  const topupCustom = () => {
    const eur = parseFloat(customTopup);
    if (!isFinite(eur) || eur < 1) { toast.error("Enter at least €1"); return; }
    if (eur > 500) { toast.error("Max top-up is €500"); return; }
    topup(Math.round(eur * 100));
  };

  const startCashout = async () => {
    const eur = parseFloat(cashoutAmount);
    if (!isFinite(eur) || eur < 1) { toast.error("Enter at least €1"); return; }
    const cents = Math.round(eur * 100);
    if (cents > (wallet?.balance_cents || 0)) {
      toast.error("Amount exceeds your wallet balance");
      return;
    }
    setBusy("cashout");
    try {
      const r = await api.post("/wallet/cashout", { amount_cents: cents });
      if (r.data.status === "paid") toast.success("Cash-out paid to your bank");
      else toast.success(r.data.message || "Cash-out queued");
      setCashoutAmount("");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Cash-out failed");
    } finally {
      setBusy(null);
    }
  };

  const startConnect = async () => {
    setBusy("connect");
    try {
      const r = await api.post("/wallet/connect/onboard", {
        origin_url: window.location.origin,
      });
      if (r.data.url) { window.location.href = r.data.url; return; }
      toast.message(r.data.message || "Bank onboarding is in manual-review mode.");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start onboarding");
    } finally {
      setBusy(null);
    }
  };

  const openStripeDashboard = async () => {
    setBusy("dashboard");
    try {
      const r = await api.post("/wallet/connect/dashboard");
      window.location.href = r.data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not open Stripe");
      setBusy(null);
    }
  };

  const balance = wallet?.balance_cents || 0;
  const payoutsReady = connect?.payouts_enabled;
  const manualMode = connect?.mode === "manual_review";

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F3F4F6]">
      <header className="px-6 py-4 border-b border-[#1E1E1E] flex items-center gap-3 sticky top-0 bg-[#0A0A0A]/90 backdrop-blur z-10">
        <Link to="/app" data-testid="wallet-back-link">
          <Button variant="ghost" size="icon" className="text-[#9CA3AF] hover:bg-[#1E1E1E]">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <h1 className="font-heading text-xl font-bold flex items-center gap-2">
          <WalletIcon className="h-5 w-5 text-[#3B82F6]" /> Wallet
        </h1>
      </header>

      {polling && (
        <div className="mx-auto max-w-4xl mt-4 px-4 py-3 rounded-2xl border border-[#2563EB]/50 bg-[#2563EB]/10 text-[#D1D5DB] text-sm flex items-center gap-3">
          <Loader2 className="h-4 w-4 animate-spin text-[#3B82F6]" />
          Confirming your top-up with Stripe…
        </div>
      )}

      <main className="max-w-4xl mx-auto p-6 space-y-8">
        {/* Balance hero */}
        <section className="rounded-3xl bg-gradient-to-br from-[#1E293B] via-[#121826] to-[#0A0A0A] border border-[#27272A] p-8 relative overflow-hidden">
          <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-[#3B82F6]/10 blur-3xl pointer-events-none" />
          <div className="relative">
            <div className="text-[10px] uppercase tracking-widest text-[#9CA3AF] mb-2">Available balance</div>
            <div data-testid="wallet-balance" className="text-5xl font-bold tabular-nums">{fmt(balance)}</div>
            <div className="text-[11px] text-[#6B7280] mt-1.5">EUR · updates instantly on top-ups and sales</div>
          </div>
        </section>

        {/* Payout status card — tells sellers at a glance whether their bank is
            wired up and marketplace sales will land directly in their Stripe account */}
        <section
          data-testid="payout-status-card"
          className="rounded-2xl bg-[#121212] border border-[#1E1E1E] p-5 flex items-start gap-4"
        >
          <div className={`h-10 w-10 rounded-full flex items-center justify-center shrink-0 ${
            connect?.payouts_enabled
              ? "bg-[#10B981]/15 text-[#10B981]"
              : "bg-[#F59E0B]/15 text-[#F59E0B]"
          }`}>
            <Landmark className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-heading font-semibold">Payout status</h2>
              <span
                data-testid="payout-status-badge"
                className={`text-[10px] px-2 py-0.5 rounded-full border ${
                  connect?.payouts_enabled
                    ? "bg-[#10B981]/15 border-[#10B981]/40 text-[#10B981]"
                    : "bg-[#F59E0B]/15 border-[#F59E0B]/40 text-[#F59E0B]"
                }`}
              >
                {connect?.payouts_enabled ? "Connected" : "Not connected"}
              </span>
            </div>
            <p className="text-xs text-[#9CA3AF] mt-1 leading-relaxed">
              {connect?.payouts_enabled
                ? "Marketplace sales are routed straight to your bank via Stripe Connect — the platform keeps 20%, you get 80% automatically."
                : "Hook up a bank account below to receive marketplace earnings directly. Until then, sales land in your NoteLosh wallet and you can cash out manually."}
            </p>
          </div>
        </section>

        {/* Top-up */}
        <section className="rounded-2xl bg-[#121212] border border-[#1E1E1E] p-6 space-y-4">
          <div className="flex items-center gap-2">
            <Plus className="h-4 w-4 text-[#3B82F6]" />
            <h2 className="font-heading font-semibold">Add money</h2>
          </div>
          {native ? <WebTopupNotice onReturn={refresh} /> : (<>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {TOPUP_PRESETS.map((c) => (
              <Button
                key={c}
                data-testid={`topup-${c}`}
                onClick={() => topup(c)}
                disabled={!!busy}
                className="h-14 rounded-2xl bg-[#1E1E1E] border border-[#27272A] hover:border-[#3B82F6] hover:bg-[#27272A] text-[#F3F4F6] text-base font-semibold"
              >
                {busy === `topup_${c}` ? <Loader2 className="h-4 w-4 animate-spin" /> : fmt(c)}
              </Button>
            ))}
          </div>
          <div className="flex gap-2 items-center">
            <div className="relative flex-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] text-sm">€</span>
              <input
                data-testid="topup-custom-input"
                type="number"
                min="1"
                max="500"
                step="1"
                value={customTopup}
                onChange={(e) => setCustomTopup(e.target.value)}
                placeholder="Other amount"
                className="w-full pl-7 h-11 bg-[#1E1E1E] border border-[#27272A] rounded-xl text-sm text-[#F3F4F6] placeholder:text-[#6B7280] focus:outline-none focus:border-[#3B82F6]"
              />
            </div>
            <Button
              data-testid="topup-custom-btn"
              onClick={topupCustom}
              disabled={!!busy || !customTopup}
              className="h-11 rounded-xl bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
            >
              Top up
            </Button>
          </div>
          <div className="text-[11px] text-[#6B7280]">
            Secure Stripe Checkout · EUR only
          </div>
          </>)}
        </section>

        {/* Cash-out */}
        <section className="rounded-2xl bg-[#121212] border border-[#1E1E1E] p-6 space-y-4">
          <div className="flex items-center gap-2">
            <ArrowDownToLine className="h-4 w-4 text-[#F59E0B]" />
            <h2 className="font-heading font-semibold">Withdraw earnings</h2>
          </div>

          {!payoutsReady && (
            <div className="rounded-xl bg-[#1E1E1E] border border-[#27272A] p-4 space-y-3">
              <div className="flex items-start gap-3">
                <Landmark className="h-5 w-5 text-[#3B82F6] mt-0.5 shrink-0" />
                <div className="flex-1 text-sm text-[#D1D5DB]">
                  <div className="font-medium text-[#F3F4F6] mb-1">{connect?.connected ? "Finish connecting your bank" : "Connect a bank first"}</div>
                  Bank onboarding takes ~2 minutes via Stripe. On Stripe's first screen, under "Type of business", pick <span className="text-[#F3F4F6]">Individual</span> if you sell as yourself, or Business if you have a company. Once verified, cash-outs land in 1–3 business days. Stripe fees come out of the 20% NoteLosh platform commission, not your 80%.
                </div>
              </div>
              <Button
                data-testid="connect-onboard-btn"
                onClick={startConnect}
                disabled={busy === "connect"}
                className="w-full rounded-xl bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
              >
                {busy === "connect" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Connect a bank via Stripe"}
              </Button>
            </div>
          )}

          {manualMode && (
            <div className="rounded-xl bg-[#F59E0B]/10 border border-[#F59E0B]/30 p-3 text-xs text-[#F59E0B] flex items-start gap-2">
              <Clock className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>Cash-outs are currently in manual-review mode. Requests are processed within 3 business days by the NoteLosh team while Stripe Connect finishes provisioning.</span>
            </div>
          )}

          <div className="flex gap-2 items-center">
            <div className="relative flex-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] text-sm">€</span>
              <input
                data-testid="cashout-amount-input"
                type="number"
                min="1"
                step="1"
                max={balance / 100}
                value={cashoutAmount}
                onChange={(e) => setCashoutAmount(e.target.value)}
                placeholder={`Withdraw amount (max ${fmt(balance)})`}
                className="w-full pl-7 h-11 bg-[#1E1E1E] border border-[#27272A] rounded-xl text-sm text-[#F3F4F6] placeholder:text-[#6B7280] focus:outline-none focus:border-[#F59E0B]"
              />
            </div>
            <Button
              data-testid="cashout-submit-btn"
              onClick={startCashout}
              disabled={busy === "cashout" || balance < 100}
              className="h-11 rounded-xl bg-[#F59E0B] hover:bg-[#D97706] text-black font-semibold"
            >
              {busy === "cashout" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Cash out"}
            </Button>
          </div>
          <p data-testid="cashout-stripe-tracking-note" className="mt-3 text-[11px] leading-relaxed text-[#9CA3AF]">
            After you cash out, you can follow your payout step by step, from processing to arriving in your bank, in your Stripe Express account.
          </p>
          {(payoutsReady || connect?.details_submitted) && (
            <Button type="button" variant="outline" data-testid="view-stripe-payouts-btn" onClick={openStripeDashboard} disabled={busy === "dashboard"}
              className="mt-3 w-full h-10 rounded-xl border-[#27272A] bg-transparent text-[#F3F4F6] hover:bg-[#1E1E1E] hover:text-white">
              {busy === "dashboard" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ExternalLink className="h-4 w-4 mr-1.5" /> View payouts in Stripe</>}
            </Button>
          )}
          {connect?.connected && connect?.mode === "connect" && <DisconnectBank onDone={refresh} />}
        </section>

        {/* Transactions */}
        <section>
          <h2 className="font-heading font-semibold mb-3 flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-[#3B82F6]" /> Recent activity
          </h2>
          {ledger.length === 0 && cashouts.length === 0 ? (
            <div className="rounded-2xl bg-[#121212] border border-dashed border-[#27272A] py-10 text-center text-sm text-[#6B7280]">
              No activity yet. Top up, buy a note pack, or wait for a sale to land your first credit.
            </div>
          ) : (
            <ul className="rounded-2xl bg-[#121212] border border-[#1E1E1E] divide-y divide-[#1E1E1E]">
              {ledger.map((e) => (
                <li key={e.entry_id} data-testid={`ledger-${e.entry_id}`} className="flex items-center gap-3 p-4">
                  <div className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 ${
                    e.direction === "credit" ? "bg-[#10B981]/15 text-[#10B981]" : "bg-[#EF4444]/15 text-[#F87171]"
                  }`}>
                    {e.kind === "marketplace_sale" ? <ShoppingBag className="h-4 w-4" />
                      : e.kind === "topup" ? <Plus className="h-4 w-4" />
                      : e.kind === "cashout" ? <ArrowDownToLine className="h-4 w-4" />
                      : <TrendingUp className="h-4 w-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-[#F3F4F6] truncate">{e.note || e.kind}</div>
                    <div className="text-[11px] text-[#6B7280]">
                      {new Date(e.created_at).toLocaleString()} · {e.kind}
                    </div>
                  </div>
                  <div className={`text-sm font-semibold tabular-nums shrink-0 ${
                    e.direction === "credit" ? "text-[#10B981]" : "text-[#F87171]"
                  }`}>
                    {e.direction === "credit" ? "+" : "−"}{fmt(e.amount_cents)}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>
    </div>
  );
}
